/**
 * Type that represents the differences between two objects
 */
export interface ObjectDiff {
  /** Properties that were added in the first object */
  added: Record<string, any>
  /** Properties that were modified in the first object */
  modified: Record<string, { oldValue: any; newValue: any }>
  /** Properties that were removed from the first object */
  removed: Record<string, any>
  /** Indicates if there was any error during comparison */
  hasError?: boolean
  /** Error message, if any */
  errorMessage?: string
}

/**
 * Configuration options for object comparison
 */
interface DiffOptions {
  /** Maximum depth for comparison (default: 10) */
  maxDepth?: number
  /** Timeout in milliseconds for the operation (default: 5000) */
  timeout?: number
}

/**
 * Checks if a value is a plain object (not array, not Date, not null)
 */
function isPlainObject(value: any): boolean {
  try {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return false
    }

    // Check for specific types that are not plain objects
    if (
      value instanceof Date ||
      value instanceof RegExp ||
      value instanceof Error
    ) {
      return false
    }

    const proto = Object.getPrototypeOf(value)
    // Allow objects with null prototype (created with Object.create(null))
    // or objects with Object.prototype as their prototype
    return (
      proto === null ||
      proto === Object.prototype ||
      Object.prototype.toString.call(value) === '[object Object]'
    )
  } catch (error) {
    return false
  }
}

/**
 * Gets object properties safely
 */
function getObjectKeys(obj: any): string[] {
  try {
    return Object.keys(obj || {})
  } catch (error) {
    return []
  }
}

/**
 * Gets property value safely
 */
function getPropertyValue(
  obj: any,
  key: string
): { value: any; hasError: boolean } {
  try {
    return { value: obj[key], hasError: false }
  } catch (error) {
    return { value: undefined, hasError: true }
  }
}

/**
 * Checks if two values are deeply equal with protection against infinite loops
 */
function deepEqual(
  a: any,
  b: any,
  visited: WeakSet<object> = new WeakSet(),
  depth: number = 0,
  maxDepth: number = 10
): boolean {
  try {
    // Protection against excessive depth
    if (depth > maxDepth) {
      return false
    }

    if (a === b) return true

    // Verification of primitive types and null/undefined
    if (a == null || b == null) return a === b
    if (typeof a !== typeof b) return false
    if (typeof a !== 'object') return a === b

    // Protection against circular references
    if (visited.has(a) || visited.has(b)) {
      return a === b // If already visited, assume they're equal if they're the same reference
    }

    // Add to visited before recursion
    if (typeof a === 'object' && a !== null) visited.add(a)
    if (typeof b === 'object' && b !== null) visited.add(b)

    // Special handling for Dates
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime()
    }

    // Handling for Arrays
    if (Array.isArray(a) !== Array.isArray(b)) return false

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i], visited, depth + 1, maxDepth)) return false
      }
      return true
    }

    // Handling for plain objects
    if (isPlainObject(a) && isPlainObject(b)) {
      const keysA = getObjectKeys(a)
      const keysB = getObjectKeys(b)

      if (keysA.length !== keysB.length) return false

      for (const key of keysA) {
        if (!keysB.includes(key)) return false

        const valueA = getPropertyValue(a, key)
        const valueB = getPropertyValue(b, key)

        if (valueA.hasError || valueB.hasError) return false
        if (
          !deepEqual(valueA.value, valueB.value, visited, depth + 1, maxDepth)
        )
          return false
      }
      return true
    }

    return false
  } catch (error) {
    return false
  }
}

/**
 * Recursively compares nested objects and adds differences to the diff object using dot notation
 */
function addNestedDifferences(
  newObj: any,
  oldObj: any,
  diff: ObjectDiff,
  basePath: string = '',
  visited: WeakSet<object> = new WeakSet(),
  depth: number = 0,
  maxDepth: number = 10
): void {
  if (depth > maxDepth) return

  // Protection against circular references
  if (typeof newObj === 'object' && newObj !== null && visited.has(newObj)) {
    return
  }
  if (typeof newObj === 'object' && newObj !== null) {
    visited.add(newObj)
  }

  // If both are plain objects, compare recursively
  if (isPlainObject(newObj) && isPlainObject(oldObj)) {
    const newKeys = getObjectKeys(newObj)
    const oldKeys = getObjectKeys(oldObj)
    const allKeys = new Set([...newKeys, ...oldKeys])

    for (const key of allKeys) {
      const currentPath = basePath ? `${basePath}.${key}` : key
      const hasNewProperty = Object.prototype.hasOwnProperty.call(newObj, key)
      const hasOldProperty = Object.prototype.hasOwnProperty.call(oldObj, key)

      if (hasNewProperty && !hasOldProperty) {
        // Property was added - if it's an object, add its nested properties with dot notation
        if (isPlainObject(newObj[key])) {
          addAllNestedProperties(newObj[key], diff.added, currentPath)
        } else {
          diff.added[currentPath] = newObj[key]
        }
      } else if (!hasNewProperty && hasOldProperty) {
        // Property was removed - if it's an object, add its nested properties with dot notation
        if (isPlainObject(oldObj[key])) {
          addAllNestedProperties(oldObj[key], diff.removed, currentPath)
        } else {
          diff.removed[currentPath] = oldObj[key]
        }
      } else if (hasNewProperty && hasOldProperty) {
        const newValue = newObj[key]
        const oldValue = oldObj[key]

        // If both values are plain objects, recurse
        if (isPlainObject(newValue) && isPlainObject(oldValue)) {
          addNestedDifferences(
            newValue,
            oldValue,
            diff,
            currentPath,
            visited,
            depth + 1,
            maxDepth
          )
        } else {
          // Compare primitive values or non-plain objects
          if (!deepEqual(newValue, oldValue, new WeakSet(), 0, maxDepth)) {
            diff.modified[currentPath] = {
              oldValue,
              newValue,
            }
          }
        }
      }
    }
  } else {
    // Not both plain objects, treat as a single value change
    if (!deepEqual(newObj, oldObj, new WeakSet(), 0, maxDepth)) {
      diff.modified[basePath || 'root'] = {
        oldValue: oldObj,
        newValue: newObj,
      }
    }
  }
}

/**
 * Helper function to add all nested properties of an object to a target with dot notation
 */
function addAllNestedProperties(
  obj: any,
  target: Record<string, any>,
  basePath: string
): void {
  if (!isPlainObject(obj)) {
    target[basePath] = obj
    return
  }

  const keys = getObjectKeys(obj)
  for (const key of keys) {
    const currentPath = `${basePath}.${key}`
    const value = obj[key]

    if (isPlainObject(value)) {
      addAllNestedProperties(value, target, currentPath)
    } else {
      target[currentPath] = value
    }
  }
}

/**
 * Compares two objects and returns the differences of the first relative to the second
 *
 * @param newObj - The current object (first object)
 * @param oldObj - The reference object (second object)
 * @param options - Configuration options for comparison
 * @returns An object containing the added, modified and removed properties
 *
 * @example
 * ```typescript
 * const oldObj = { a: 1, b: 2, c: 3 }
 * const newObj = { a: 1, b: 5, d: 4 }
 *
 * const diff = getObjectDiff(newObj, oldObj)
 * // Result:
 * // {
 * //   added: { d: 4 },
 * //   modified: { b: { oldValue: 2, newValue: 5 } },
 * //   removed: { c: 3 }
 * // }
 * ```
 *
 * @example
 * ```typescript
 * const oldObj = {
 *   general: {
 *     active: true,
 *     description: "Tem descrição",
 *     name: "Cópia de Acumulo com preço manual"
 *   }
 * }
 * const newObj = {
 *   general: {
 *     active: true,
 *     description: "Tem descrição",
 *     name: "Cópia de Acumulo com preço manua"
 *   }
 * }
 *
 * const diff = getObjectDiff(newObj, oldObj)
 * // Result:
 * // {
 * //   added: {},
 * //   modified: {
 * //     "general.name": {
 * //       oldValue: "Cópia de Acumulo com preço manual",
 * //       newValue: "Cópia de Acumulo com preço manua"
 * //     }
 * //   },
 * //   removed: {}
 * // }
 * ```
 */
export function getObjectDiff(
  newObj: Record<string, any>,
  oldObj: Record<string, any>,
  options: DiffOptions = {}
): ObjectDiff {
  const { maxDepth = 10, timeout = 1000 } = options
  const startTime = Date.now()

  const diff: ObjectDiff = {
    added: {},
    modified: {},
    removed: {},
    hasError: false,
    errorMessage: undefined,
  }

  try {
    // Timeout check
    const checkTimeout = () => {
      if (Date.now() - startTime > timeout) {
        throw new Error(`Operation exceeded ${timeout}ms timeout`)
      }
    }

    // If one of the objects is null/undefined, treat as complete difference
    if (!newObj && !oldObj) {
      return diff
    }

    if (!newObj) {
      try {
        diff.removed = { ...oldObj }
      } catch (error) {
        diff.hasError = true
        diff.errorMessage = 'Error processing reference object'
      }
      return diff
    }

    if (!oldObj) {
      try {
        diff.added = { ...newObj }
      } catch (error) {
        diff.hasError = true
        diff.errorMessage = 'Error processing current object'
      }
      return diff
    }

    checkTimeout()

    // Use the new nested comparison function
    addNestedDifferences(newObj, oldObj, diff, '', new WeakSet(), 0, maxDepth)

    return diff
  } catch (error) {
    return {
      added: {},
      modified: {},
      removed: {},
      hasError: true,
      errorMessage:
        error instanceof Error
          ? error.message
          : 'Unknown error during comparison',
    }
  }
}

/**
 * Checks if there are any differences between two objects
 *
 * @param newObj - The current object
 * @param oldObj - The reference object
 * @param options - Configuration options for comparison
 * @returns true if there are differences, false otherwise
 */
export function hasObjectDiff(
  newObj: Record<string, any>,
  oldObj: Record<string, any>,
  options: DiffOptions = {}
): boolean {
  try {
    const diff = getObjectDiff(newObj, oldObj, options)

    // If there was an error, consider it as having a difference
    if (diff.hasError) {
      return true
    }

    return (
      Object.keys(diff.added).length > 0 ||
      Object.keys(diff.modified).length > 0 ||
      Object.keys(diff.removed).length > 0
    )
  } catch (error) {
    // In case of error, assume there is a difference
    return true
  }
}

/**
 * Creates a textual summary of the differences between two objects
 *
 * @param newObj - The current object
 * @param oldObj - The reference object
 * @param options - Configuration options for comparison
 * @returns A string describing the differences
 */
export function getObjectDiffSummary(
  newObj: Record<string, any>,
  oldObj: Record<string, any>,
  options: DiffOptions = {}
): string {
  try {
    const diff = getObjectDiff(newObj, oldObj, options)

    if (diff.hasError) {
      return `Comparison error: ${diff.errorMessage}`
    }

    const changes: string[] = []

    const addedCount = Object.keys(diff.added).length
    const modifiedCount = Object.keys(diff.modified).length
    const removedCount = Object.keys(diff.removed).length

    if (addedCount > 0) {
      changes.push(`${addedCount} property(ies) added`)
    }

    if (modifiedCount > 0) {
      changes.push(`${modifiedCount} property(ies) modified`)
    }

    if (removedCount > 0) {
      changes.push(`${removedCount} property(ies) removed`)
    }

    return changes.length > 0 ? changes.join(', ') : 'No differences found'
  } catch (error) {
    return `Unexpected error in comparison: ${
      error instanceof Error ? error.message : 'Unknown error'
    }`
  }
}

/**
 * Utility function to create safe circular objects for testing
 * This function is useful for tests and development
 */
export function createSafeCircularObject(
  data: Record<string, any>
): Record<string, any> {
  try {
    return JSON.parse(JSON.stringify(data))
  } catch (error) {
    return {}
  }
}

/**
 * Checks if an object has circular references
 *
 * @param obj - Object to be checked
 * @returns true if it has circular references
 */
export function hasCircularReference(obj: any): boolean {
  try {
    JSON.stringify(obj)
    return false
  } catch (error) {
    return error instanceof TypeError && error.message.includes('circular')
  }
}

/**
 * Example of safe usage of the getObjectDiff function:
 *
 * ```typescript
 * const options = {
 *   maxDepth: 5,     // Limits comparison depth
 *   timeout: 3000    // 3 second timeout
 * }
 *
 * const diff = getObjectDiff(newObj, oldObj, options)
 *
 * if (diff.hasError) {
 *   console.error('Comparison error:', diff.errorMessage)
 * } else {
 *   console.log('Differences found:', diff)
 * }
 * ```
 *
 * Implemented protections:
 * - ✅ Protection against circular references
 * - ✅ Configurable depth limit
 * - ✅ Timeout for long operations
 * - ✅ Try-catch for property access
 * - ✅ More robust type checking
 * - ✅ Handling of proxy and special objects
 * - ✅ Detailed error reporting
 */
